/**
 * Live Event Pool Component
 * VOY-133: Time-limited special pools with premium prizes
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Zap, Clock, Trophy, Bell } from 'lucide-react';

interface LiveEventPoolProps {
  title: string;
  prize: string;
  value: number;
  multiplier: number; // e.g., 3 = 3x normal value
  currentPlayers: number;
  requiredPlayers: number;
  entryFee: number;
  startsIn: string;
  onReserveSpot: () => void;
  onSetReminder: () => void;
}

export function LiveEventPool({
  title,
  prize,
  value,
  multiplier,
  currentPlayers,
  requiredPlayers,
  entryFee,
  startsIn,
  onReserveSpot,
  onSetReminder
}: LiveEventPoolProps) {
  const progress = (currentPlayers / requiredPlayers) * 100;
  const isFilling = progress > 90;

  return (
    <Card className={`rounded-3xl border-2 ${isFilling ? 'border-orange-500 animate-pulse' : 'border-yellow-500'} bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl`}>
      <CardContent className="p-5">
        {/* Flash Badge */}
        <div className="flex items-center gap-2 mb-3">
          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 animate-pulse">
            <Zap className="w-3 h-3" />
            FLASH POOL
          </div>
          {multiplier > 1 && (
            <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">
              {multiplier}x VALUE!
            </div>
          )}
        </div>

        {/* Title & Prize */}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            <p className="font-semibold text-gray-700 dark:text-zinc-300">{prize}</p>
          </div>
          <p className="text-sm text-gray-600 dark:text-zinc-400 mt-1">
            Value: <span className="font-bold text-green-600 dark:text-green-400">${value.toLocaleString()}</span>
          </p>
        </div>

        {/* Countdown */}
        <div className="bg-white dark:bg-zinc-700/50 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              <span className="font-semibold text-gray-700 dark:text-white">Starts in:</span>
            </div>
            <span className="text-2xl font-bold text-orange-500">{startsIn}</span>
          </div>
        </div>

        {/* Player Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-700 dark:text-zinc-300">Reserved Spots</span>
            <span className="font-semibold dark:text-white">
              {currentPlayers.toLocaleString()} / {requiredPlayers.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-zinc-700 rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          {isFilling && (
            <p className="text-xs text-orange-600 dark:text-orange-400 font-semibold mt-1 text-center animate-pulse">
              ⚡ Almost Full - Reserve Now!
            </p>
          )}
        </div>

        {/* Entry Fee */}
        <div className="bg-yellow-100 dark:bg-zinc-700/50 border border-yellow-300 dark:border-zinc-600 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-yellow-900 dark:text-white">Entry Fee</p>
              <p className="text-xs text-yellow-700 dark:text-zinc-400">Limited event pricing</p>
            </div>
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">${entryFee}</p>
          </div>
        </div>

        {/* Special Rules */}
        <div className="bg-red-50 dark:bg-zinc-700/50 border border-red-200 dark:border-zinc-600 rounded-lg p-3 mb-4">
          <p className="text-xs text-red-800 dark:text-red-400">
            🏆 <strong>Winner Takes All!</strong> Top 2 win - No participation prize
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={onReserveSpot}
            className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-full"
          >
            Reserve Spot
          </Button>
          <Button
            onClick={onSetReminder}
            variant="outline"
            className="rounded-full border-orange-500 text-orange-600"
          >
            <Bell className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

